import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { User, LoginRequest } from '../types'
import * as authApi from '../api/auth'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

async function loadCurrentUser(): Promise<User | null> {
  try {
    await authApi.refreshToken()
    return await authApi.getMe()
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    setUser(await loadCurrentUser())
  }, [])

  useEffect(() => {
    let isActive = true

    void loadCurrentUser()
      .then((nextUser) => {
        if (isActive) {
          setUser(nextUser)
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [])

  const login = async (credentials: LoginRequest) => {
    const response = await authApi.login(credentials)
    setUser(response.user)
  }

  const logout = async () => {
    try { await authApi.logout() } catch { /* Always clear local state even if server fails */ }
    setUser(null)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// useAuth must stay beside the private context unless this module is split.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
